/**
 * WhatsAppButton - Componente para envio de mensagens WhatsApp
 * Integrado com templates de garantia, status de reparação e notificações
 */
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  Alert,
  AlertDescription,
  AlertTitle
} from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { whatsappAPI } from '@/lib/api';
import { 
  MessageCircle, 
  Send, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Clock,
  Wrench,
  Package,
  Edit,
  Loader2,
  QrCode,
  ExternalLink,
  UserPlus,
  Info
} from 'lucide-react';

// Sandbox configuration
const SANDBOX_NUMBER = "+14155238886";
const SANDBOX_JOIN_CODE = "join giving-satisfied";
const SANDBOX_JOIN_LINK = `https://wa.me/14155238886?text=${encodeURIComponent(SANDBOX_JOIN_CODE)}`;

// Template definitions with icons and colors
const TEMPLATES = {
  // Warranty responses
  warranty_accepted: {
    name: 'Garantia Aceite',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    category: 'warranty',
    description: 'Reparação gratuita sob garantia'
  },
  warranty_rejected: {
    name: 'Garantia Expirada',
    icon: Clock,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50',
    category: 'warranty',
    description: 'Fora do prazo - enviar orçamento'
  },
  warranty_void: {
    name: 'Garantia Anulada',
    icon: XCircle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    category: 'warranty',
    description: 'Dano não coberto - enviar orçamento'
  },
  // Repair status
  repair_received: {
    name: 'Equipamento Recebido',
    icon: Package,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    category: 'repair',
    description: 'Confirmar recepção do equipamento'
  },
  repair_in_progress: {
    name: 'Reparação em Curso',
    icon: Wrench,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    category: 'repair',
    description: 'Informar que está a ser reparado'
  },
  repair_ready: {
    name: 'Pronto para Levantamento',
    icon: CheckCircle,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
    category: 'repair',
    description: 'Equipamento pronto - chamar cliente'
  }
};

// Generate message preview based on template and service data
const generatePreview = (templateType, service, extraVariables = {}) => {
  const templates = {
    warranty_accepted: `✅ *CG Business - Garantia Aprovada*

Prezado(a) ${service?.customer_name || 'Cliente'},

A sua reclamação de garantia foi *ACEITE*.

📱 *Equipamento:* ${service?.device_brand || ''} ${service?.device_model || ''}
🔧 *Problema:* ${service?.issue_description || ''}
📋 *Nº Serviço:* ${service?.service_number || ''}

O seu equipamento será reparado *sem custos adicionais*.

⏱️ *Prazo estimado:* ${extraVariables.estimated_days || '3-5'} dias úteis

_CG Business_`,

    warranty_rejected: `⚠️ *CG Business - Garantia Expirada*

Prezado(a) ${service?.customer_name || 'Cliente'},

A garantia do seu equipamento *expirou*.

📱 *Equipamento:* ${service?.device_brand || ''} ${service?.device_model || ''}

💰 *Orçamento de Reparação:*
• Diagnóstico: ${service?.diagnostic_cost?.toLocaleString() || '0'} MT
• Total estimado: ${service?.final_cost?.toLocaleString() || '0'} MT

Responda *SIM* para aprovar.

_CG Business_`,

    warranty_void: `❌ *CG Business - Garantia Não Aplicável*

Prezado(a) ${service?.customer_name || 'Cliente'},

O defeito *não está coberto* pela garantia.

📱 *Equipamento:* ${service?.device_brand || ''} ${service?.device_model || ''}
🔍 *Motivo:* ${extraVariables.void_reason || 'Dano físico detectado'}

💰 *Orçamento:* ${service?.final_cost?.toLocaleString() || '0'} MT

Responda *SIM* para aprovar.

_CG Business_`,

    repair_received: `📥 *CG Business - Equipamento Recebido*

Prezado(a) ${service?.customer_name || 'Cliente'},

O seu equipamento foi recebido.

📱 *Equipamento:* ${service?.device_brand || ''} ${service?.device_model || ''}
📋 *Nº Serviço:* ${service?.service_number || ''}

Iremos analisar e entrar em contacto.

_CG Business_`,

    repair_in_progress: `🔧 *CG Business - Reparação em Curso*

Prezado(a) ${service?.customer_name || 'Cliente'},

A reparação está *em andamento*.

📱 *Equipamento:* ${service?.device_brand || ''} ${service?.device_model || ''}
📋 *Nº Serviço:* ${service?.service_number || ''}
⏱️ *Previsão:* ${extraVariables.estimated_completion || '2-3 dias úteis'}

_CG Business_`,

    repair_ready: `✅ *CG Business - Equipamento Pronto!*

Prezado(a) ${service?.customer_name || 'Cliente'},

O seu equipamento está *pronto*! 🎉

📱 *Equipamento:* ${service?.device_brand || ''} ${service?.device_model || ''}
📋 *Nº Serviço:* ${service?.service_number || ''}
💰 *Valor:* ${service?.final_cost?.toLocaleString() || '0'} MT

📍 *Local:* ${service?.location_name || 'CG Business'}

_CG Business_`
  };

  return templates[templateType] || '';
};

/**
 * WhatsAppButton - Dropdown button for sending WhatsApp messages
 */
export function WhatsAppButton({ service, onMessageSent, size = 'sm', variant = 'outline' }) {
  const [isLoading, setIsLoading] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [sandboxDialog, setSandboxDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [messagePreview, setMessagePreview] = useState('');
  const [extraVariables, setExtraVariables] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [sandboxStatus, setSandboxStatus] = useState(null);
  const [checkingRegistration, setCheckingRegistration] = useState(false);

  const hasPhone = service?.customer_phone;

  // Check if phone is registered in Sandbox
  const checkSandboxRegistration = async () => {
    if (!hasPhone) return;
    
    setCheckingRegistration(true);
    try {
      const response = await whatsappAPI.checkSandboxRegistration(service.customer_phone);
      setSandboxStatus(response.data);
    } catch (error) {
      console.error('Error checking sandbox registration:', error);
      // Assume not registered if error
      setSandboxStatus({ is_registered: false });
    } finally {
      setCheckingRegistration(false);
    }
  };

  const handleTemplateSelect = async (templateType) => {
    // First check sandbox registration
    await checkSandboxRegistration();
    
    const preview = generatePreview(templateType, service, {});
    setSelectedTemplate(templateType);
    setMessagePreview(preview);
    setExtraVariables({});
    setIsEditing(false);
    setPreviewDialog(true);
  };

  const handleRegisterAndContinue = async () => {
    try {
      await whatsappAPI.registerSandboxNumber(service.customer_phone);
      setSandboxStatus({ is_registered: true });
      toast.success('Número marcado como registado!');
    } catch (error) {
      toast.error('Erro ao registar número');
    }
  };

  const handleSend = async () => {
    if (!selectedTemplate || !hasPhone) return;

    setIsLoading(true);
    try {
      const template = TEMPLATES[selectedTemplate];
      
      if (template.category === 'warranty') {
        await whatsappAPI.sendWarrantyResponse(service.id, selectedTemplate, extraVariables);
      } else {
        await whatsappAPI.sendRepairStatus(service.id, selectedTemplate);
      }

      toast.success('Mensagem WhatsApp enviada!', {
        description: `${template.name} enviado para ${service.customer_name}`
      });

      setPreviewDialog(false);
      if (onMessageSent) onMessageSent();
    } catch (error) {
      console.error('WhatsApp send error:', error);
      toast.error('Erro ao enviar mensagem', {
        description: error.response?.data?.detail || 'Verifique o número de telefone'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCustom = async () => {
    if (!messagePreview || !hasPhone) return;

    setIsLoading(true);
    try {
      await whatsappAPI.sendCustom({
        recipient_phone: service.customer_phone,
        message: messagePreview,
        service_id: service.id
      });

      toast.success('Mensagem personalizada enviada!');
      setPreviewDialog(false);
      if (onMessageSent) onMessageSent();
    } catch (error) {
      console.error('WhatsApp send error:', error);
      toast.error('Erro ao enviar mensagem', {
        description: error.response?.data?.detail || 'Verifique o número de telefone'
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!hasPhone) {
    return (
      <Button variant="ghost" size={size} disabled className="text-muted-foreground">
        <MessageCircle className="h-4 w-4 mr-1" />
        Sem telefone
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant={variant} size={size} className="gap-1">
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Enviar para: {service.customer_phone}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {/* Warranty Templates */}
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Respostas de Garantia
          </DropdownMenuLabel>
          {Object.entries(TEMPLATES)
            .filter(([_, t]) => t.category === 'warranty')
            .map(([key, template]) => {
              const Icon = template.icon;
              return (
                <DropdownMenuItem 
                  key={key} 
                  onClick={() => handleTemplateSelect(key)}
                  className="cursor-pointer"
                >
                  <Icon className={`h-4 w-4 mr-2 ${template.color}`} />
                  <div className="flex flex-col">
                    <span className="text-sm">{template.name}</span>
                    <span className="text-xs text-muted-foreground">{template.description}</span>
                  </div>
                </DropdownMenuItem>
              );
            })}
          
          <DropdownMenuSeparator />
          
          {/* Repair Status Templates */}
          <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
            Status de Reparação
          </DropdownMenuLabel>
          {Object.entries(TEMPLATES)
            .filter(([_, t]) => t.category === 'repair')
            .map(([key, template]) => {
              const Icon = template.icon;
              return (
                <DropdownMenuItem 
                  key={key} 
                  onClick={() => handleTemplateSelect(key)}
                  className="cursor-pointer"
                >
                  <Icon className={`h-4 w-4 mr-2 ${template.color}`} />
                  <div className="flex flex-col">
                    <span className="text-sm">{template.name}</span>
                    <span className="text-xs text-muted-foreground">{template.description}</span>
                  </div>
                </DropdownMenuItem>
              );
            })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-600" />
              Enviar WhatsApp
            </DialogTitle>
            <DialogDescription>
              {selectedTemplate && TEMPLATES[selectedTemplate]?.name} para {service?.customer_name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Sandbox Warning - Show if not registered */}
            {sandboxStatus && !sandboxStatus.is_registered && (
              <Alert className="border-amber-300 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">Número não registado no Sandbox</AlertTitle>
                <AlertDescription className="text-amber-700 space-y-3">
                  <p className="text-sm">
                    O número <strong>{service?.customer_phone}</strong> precisa de se registar no WhatsApp Sandbox antes de poder receber mensagens.
                  </p>
                  
                  <div className="bg-white rounded-lg p-3 border border-amber-200 space-y-2">
                    <p className="text-xs font-medium text-amber-900">Passos para registar:</p>
                    <ol className="text-xs space-y-1 list-decimal list-inside">
                      <li>O cliente abre o WhatsApp</li>
                      <li>Envia <code className="bg-amber-100 px-1 rounded">{SANDBOX_JOIN_CODE}</code> para <strong>{SANDBOX_NUMBER}</strong></li>
                      <li>Aguarda confirmação &ldquo;You are all set!&rdquo;</li>
                    </ol>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => window.open(SANDBOX_JOIN_LINK, '_blank')}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      Abrir Link WhatsApp
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => setSandboxDialog(true)}
                    >
                      <QrCode className="h-3 w-3 mr-1" />
                      Ver QR Code
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      className="text-xs bg-green-600 hover:bg-green-700"
                      onClick={handleRegisterAndContinue}
                    >
                      <UserPlus className="h-3 w-3 mr-1" />
                      Já Registou? Confirmar
                    </Button>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Registered badge */}
            {sandboxStatus?.is_registered && (
              <div className="flex items-center gap-2 p-2 bg-green-50 rounded-lg border border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-700">Número registado no Sandbox</span>
              </div>
            )}

            {/* Recipient Info */}
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="text-sm font-medium">{service?.customer_name}</p>
                <p className="text-xs text-muted-foreground">{service?.customer_phone}</p>
              </div>
              {selectedTemplate && (
                <Badge className={TEMPLATES[selectedTemplate]?.bgColor}>
                  {TEMPLATES[selectedTemplate]?.name}
                </Badge>
              )}
            </div>

            {/* Extra Variables (for warranty_void) */}
            {selectedTemplate === 'warranty_void' && (
              <div className="space-y-2">
                <Label className="text-sm">Motivo da Anulação</Label>
                <Input
                  value={extraVariables.void_reason || ''}
                  onChange={(e) => {
                    const newVars = { ...extraVariables, void_reason: e.target.value };
                    setExtraVariables(newVars);
                    setMessagePreview(generatePreview(selectedTemplate, service, newVars));
                  }}
                  placeholder="Ex: Dano por queda, Contacto com líquidos..."
                />
              </div>
            )}

            {/* Message Preview */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Pré-visualização da Mensagem</Label>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setIsEditing(!isEditing)}
                  className="h-7 text-xs"
                >
                  <Edit className="h-3 w-3 mr-1" />
                  {isEditing ? 'Concluir' : 'Editar'}
                </Button>
              </div>
              <Textarea
                value={messagePreview}
                onChange={(e) => setMessagePreview(e.target.value)}
                readOnly={!isEditing}
                className={`min-h-[250px] text-sm font-mono ${!isEditing ? 'bg-slate-50' : ''}`}
              />
              {isEditing && (
                <p className="text-xs text-amber-600">
                  ⚠️ Mensagem editada - será enviada como mensagem personalizada
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPreviewDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={isEditing ? handleSendCustom : handleSend}
              disabled={isLoading || (sandboxStatus && !sandboxStatus.is_registered)}
              className="bg-green-600 hover:bg-green-700"
              title={sandboxStatus && !sandboxStatus.is_registered ? "Número não registado no Sandbox" : ""}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Enviar WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={sandboxDialog} onOpenChange={setSandboxDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-green-600" />
              Registar no WhatsApp Sandbox
            </DialogTitle>
            <DialogDescription>
              Leia o QR Code com o WhatsApp para registar automaticamente
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* QR Code Image */}
            <div className="flex justify-center p-4 bg-white rounded-lg border">
              <img 
                src={`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/sandbox/qrcode`}
                alt="QR Code para registo WhatsApp Sandbox"
                className="w-48 h-48"
              />
            </div>

            {/* Manual Instructions */}
            <div className="text-center space-y-2">
              <p className="text-sm text-muted-foreground">Ou envie manualmente:</p>
              <div className="bg-slate-100 rounded-lg p-3">
                <p className="text-xs text-muted-foreground">Enviar para:</p>
                <p className="font-mono font-bold">{SANDBOX_NUMBER}</p>
                <p className="text-xs text-muted-foreground mt-2">Mensagem:</p>
                <p className="font-mono font-bold text-green-600">{SANDBOX_JOIN_CODE}</p>
              </div>
            </div>

            {/* Open WhatsApp Button */}
            <Button
              className="w-full bg-green-600 hover:bg-green-700"
              onClick={() => window.open(SANDBOX_JOIN_LINK, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Abrir WhatsApp
            </Button>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSandboxDialog(false)}>
              Fechar
            </Button>
            <Button 
              onClick={() => {
                handleRegisterAndContinue();
                setSandboxDialog(false);
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Já Registei
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * WhatsAppQuickActions - Quick action buttons for common status updates
 */
export function WhatsAppQuickActions({ service, onMessageSent }) {
  const [isLoading, setIsLoading] = useState(null);

  const quickSend = async (templateType) => {
    if (!service?.customer_phone) {
      toast.error('Cliente não tem telefone');
      return;
    }

    setIsLoading(templateType);
    try {
      const template = TEMPLATES[templateType];
      
      if (template.category === 'warranty') {
        await whatsappAPI.sendWarrantyResponse(service.id, templateType, {});
      } else {
        await whatsappAPI.sendRepairStatus(service.id, templateType);
      }

      toast.success(`${template.name} enviado!`);
      if (onMessageSent) onMessageSent();
    } catch (error) {
      toast.error('Erro ao enviar', {
        description: error.response?.data?.detail
      });
    } finally {
      setIsLoading(null);
    }
  };

  if (!service?.customer_phone) return null;

  return (
    <div className="flex gap-1 flex-wrap">
      <Button
        variant="outline"
        size="sm"
        onClick={() => quickSend('repair_received')}
        disabled={isLoading !== null}
        className="text-xs h-7"
      >
        {isLoading === 'repair_received' ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Package className="h-3 w-3 mr-1 text-blue-600" />
        )}
        Recebido
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => quickSend('repair_in_progress')}
        disabled={isLoading !== null}
        className="text-xs h-7"
      >
        {isLoading === 'repair_in_progress' ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Wrench className="h-3 w-3 mr-1 text-purple-600" />
        )}
        Em Curso
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => quickSend('repair_ready')}
        disabled={isLoading !== null}
        className="text-xs h-7"
      >
        {isLoading === 'repair_ready' ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
        )}
        Pronto
      </Button>
    </div>
  );
}

export default WhatsAppButton;
